import React from 'react'

import TitlePagePdf from 'features/planner/pages/MasterCampaignPlanPage/TitlePagePdf'
import { MediaPlanRef } from 'features/planner/pages/MasterCampaignPlanPage/components/MediaPlan'
import { generatePdfBlob } from 'utils/pdfDownloader'

import { captureFullPageScreenshotWithFallback, FullPageScreenshotResult } from '../utils/fullPageScreenshot'
import { PdfSection, downloadCombinedPdf } from '../utils/pdfCombination'
import { createScreenshotPdfSection } from '../utils/pdfPageInsertion'
import { TabNavigationHelper, TAB_CONFIGS } from '../utils/tabNavigationHelper'
import { TabPdfGenerationContext, PdfGenerationResult, generateMarketAnalysisPdf, generatePersonasPdf, generateMediaPlanPdf, generateProjectionsPdf } from './tabPdfGenerators'

export interface TabGenerationConfig {
  tabId: string
  tabName: string
  generator: (context: TabPdfGenerationContext, refs?: any) => Promise<PdfGenerationResult>
  requiresScreenshots: boolean
  refs?: any
}

interface TabScreenshotData {
  tabId: string
  tabName: string
  screenshotResult: FullPageScreenshotResult
  pdfSection?: { name: string; blob: Blob }
}

export interface MultiTabPdfProgress {
  currentTab: string
  currentStep: number
  totalSteps: number
  isComplete: boolean
  errors: string[]
}

export type ProgressCallback = (progress: MultiTabPdfProgress) => void

export class MultiTabPdfService {
  private isStaffPdf = false
  private navigationHelper: TabNavigationHelper
  private progressCallback?: ProgressCallback

  constructor(isStaffPdf: boolean, progressCallback?: ProgressCallback) {
    this.isStaffPdf = isStaffPdf
    this.navigationHelper = new TabNavigationHelper()
    this.progressCallback = progressCallback
  }

  private reportProgress(currentTab: string, currentStep: number, totalSteps: number, errors: string[] = []) {
    this.progressCallback?.({
      currentTab,
      currentStep,
      totalSteps,
      isComplete: currentStep >= totalSteps,
      errors
    })
  }

  async generateAllClientPdfs(
    context: TabPdfGenerationContext,
    onTabChange: (tabId: string) => void,
    tabRefs: {
      ganttChartRef?: React.RefObject<HTMLDivElement>
      marketAnalysisPrintRef?: React.RefObject<HTMLDivElement>
      projectionRefs?: {
        deliveryMetricsRef?: React.RefObject<HTMLDivElement>
        connectedTvRef?: React.RefObject<HTMLDivElement>
        internetDisplayRef?: React.RefObject<HTMLDivElement>
        targetedHouseholdsRef?: React.RefObject<HTMLDivElement>
        billboardLocationsRef?: React.RefObject<HTMLDivElement>
      }
      projectionMapRefs?: {
        targetedHouseholdMapRef?: React.RefObject<any>
        billboardMapRef?: React.RefObject<any>
      }
      mediaplanRef?: React.RefObject<MediaPlanRef>
    }
  ): Promise<void> {
    const originalTab = this.navigationHelper.getCurrentTabId()
    const sections: PdfSection[] = []
    const errors: string[] = []
    const screenshotData = new Map<string, TabScreenshotData>()
    const totalSteps = TAB_CONFIGS.length + 1

    try {
      const tabConfigs: TabGenerationConfig[] = [
        {
          tabId: 'executive-summary',
          tabName: 'Executive Summary',
          generator: _ctx => Promise.resolve({ success: true }),
          requiresScreenshots: this.isStaffPdf
        },
        {
          tabId: 'market-analysis',
          tabName: 'Analysis & Strategy',
          generator: ctx => generateMarketAnalysisPdf(this.isStaffPdf, ctx, tabRefs.marketAnalysisPrintRef),
          requiresScreenshots: this.isStaffPdf
        },
        {
          tabId: 'personas',
          tabName: 'Personas',
          generator: ctx => generatePersonasPdf(this.isStaffPdf, ctx),
          requiresScreenshots: this.isStaffPdf
        },
        {
          tabId: 'media-plan',
          tabName: 'Media Plan',
          generator: ctx => generateMediaPlanPdf(this.isStaffPdf, ctx, tabRefs.ganttChartRef, tabRefs.mediaplanRef),
          requiresScreenshots: this.isStaffPdf
        },
        {
          tabId: 'projections',
          tabName: 'Projections',
          generator: ctx => generateProjectionsPdf(this.isStaffPdf, ctx, tabRefs.projectionRefs, tabRefs.projectionMapRefs),
          requiresScreenshots: false
        }
      ]

      const titlePageBlob = await generatePdfBlob(
        React.createElement(TitlePagePdf, {
          organizationName: context.organizationName,
          accountName: context.accountName
        })
      )

      sections.push({
        name: 'Title Page',
        blob: titlePageBlob,
        startOnFrontPage: true
      })

      for (let i = 0; i < tabConfigs.length; i++) {
        const config = tabConfigs[i]

        this.reportProgress(config.tabName, i + 1, totalSteps, errors)

        try {
          await this.navigationHelper.navigateToTab(config.tabId, onTabChange)

          if (config.requiresScreenshots || this.isStaffPdf) {
            await this.navigationHelper.waitForTabContent(config.tabId, 1500)

            if (config.tabId === 'projections') {
              await new Promise(resolve => setTimeout(resolve, 1000))
            }
          }

          if (this.isStaffPdf && config.requiresScreenshots) {
            try {
              await new Promise(resolve => setTimeout(resolve, 800))

              const screenshotResult = await captureFullPageScreenshotWithFallback({
                tabId: config.tabId,
                width: 1600,
                height: 2000,
                quality: 100,
                timeout: 20000,
                containerSelector: 'body'
              })

              const tabScreenshot: TabScreenshotData = {
                tabId: config.tabId,
                tabName: config.tabName,
                screenshotResult
              }

              if (screenshotResult.success && screenshotResult.imageData) {
                const pdfSection = await createScreenshotPdfSection(screenshotResult.imageData, config.tabName)
                if (pdfSection) {
                  tabScreenshot.pdfSection = pdfSection
                } else {
                  throw new Error(`Failed to convert screenshot to PDF for ${config.tabName}. PDF generation cannot continue without required screenshots.`)
                }

                if (screenshotResult.imageData.startsWith('blob:')) {
                  URL.revokeObjectURL(screenshotResult.imageData)
                }
              } else {
                throw new Error(`Failed to capture screenshot for ${config.tabName}: ${screenshotResult.error || 'Unknown screenshot error'}. PDF generation cannot continue without required screenshots.`)
              }

              screenshotData.set(config.tabId, tabScreenshot)
            } catch (screenshotError) {
              const tabScreenshot = screenshotData.get(config.tabId)
              if (tabScreenshot?.screenshotResult?.imageData?.startsWith('blob:')) {
                URL.revokeObjectURL(tabScreenshot.screenshotResult.imageData)
              }
              
              const errorMsg = `Screenshot capture failed for ${config.tabName}: ${screenshotError instanceof Error ? screenshotError.message : String(screenshotError)}. PDF generation cannot continue without required screenshots.`
              throw new Error(errorMsg)
            }
          }

          const timeoutMs = config.tabId === 'projections' ? 180000 : 120000
          const result = await Promise.race([config.generator(context), new Promise<PdfGenerationResult>((_, reject) => setTimeout(() => reject(new Error(`PDF generation timeout after ${timeoutMs}ms`)), timeoutMs))])

          if (result.success) {
            let tocLabelAssignedForTab = false

            if (this.isStaffPdf) {
              const tabScreenshot = screenshotData.get(config.tabId)
              if (tabScreenshot?.pdfSection) {
                sections.push({
                  ...tabScreenshot.pdfSection,
                  startOnFrontPage: true,
                  tocLabel: config.tabName
                })
                tocLabelAssignedForTab = true
              }
            }

            if (result.blob && result.blob.size > 0) {
              sections.push({
                name: config.tabName,
                blob: result.blob,
                startOnFrontPage: true,
                ...(this.isStaffPdf && !tocLabelAssignedForTab && { tocLabel: config.tabName }),
                ...(this.isStaffPdf && config.tabId === 'projections' && { rotateLandscapeToPortrait: true })
              })
            }
          } else {
            const errorMsg = result.error || `Failed to generate ${config.tabName} PDF - empty result`
            errors.push(errorMsg)
            console.warn(`Non-fatal error in ${config.tabName}:`, errorMsg)
          }
        } catch (error) {
          const errorMsg = `Failed to generate ${config.tabName}: ${error instanceof Error ? error.message : String(error)}`
          errors.push(errorMsg)
          console.error(`Critical error in ${config.tabName}:`, {
            error: errorMsg,
            tabId: config.tabId,
            requiresScreenshots: config.requiresScreenshots,
            stack: error instanceof Error ? error.stack : undefined
          })
          
          if (this.isStaffPdf && config.requiresScreenshots && error instanceof Error && error.message.includes('screenshot')) {
            throw error
          }
        }
      }

      if (sections.length === 0) {
        console.error('All PDF sections failed to generate')
        throw new Error('No PDF sections were successfully generated. Check the console for detailed error messages.')
      }

      try {
        const filename = `${context.masterCampaignPlan.name} - Complete Campaign Plan.pdf`
        await downloadCombinedPdf(sections, filename, this.isStaffPdf)

        this.reportProgress('Complete', totalSteps, totalSteps, errors)
      } catch (combinationError) {
        const errorMsg = `Failed to combine or download PDF: ${combinationError instanceof Error ? combinationError.message : String(combinationError)}`
        errors.push(errorMsg)
        console.error('PDF combination failed:', combinationError)
        this.reportProgress('Error', totalSteps, totalSteps, errors)
        throw new Error(errorMsg)
      }
    } catch (error) {
      const errorMsg = `Failed to generate combined PDF: ${error}`
      errors.push(errorMsg)
      this.reportProgress('Error', totalSteps, totalSteps, errors)
      throw new Error(errorMsg)
    } finally {
      if (originalTab) {
        try {
          await this.navigationHelper.navigateToTab(originalTab, onTabChange)
        } catch (error) {
          console.warn('Could not return to original tab:', error)
        }
      }
    }
  }
}
